import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { lighten, styled } from "@mui/material/styles";

const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
      },
    },
    backgroundColor: primaryColor,
    color: "#FFFFFF !important",
    fontSize: "0.9rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
    whiteSpace: "normal",
    wordWrap: "break-word",
  },
  "& .last-row": {
    fontWeight: "bold",
    backgroundColor: lighten(primaryColor, 0.85),
    color: "#000",
  },
  "& .first-column": {
    fontWeight: "bold",
  },
  "& .negative-value": {
    fontWeight: "bold",
    backgroundColor: "#FF4D4D",
    color: "#fff",
  },
  "& .zero-row": {
    backgroundColor: "#E8E8E8",
    color: "#888",
  },
  "& .value-increased": {
    backgroundColor: "#FFB74D",
    color: "#000",
  },
  "& .value-decreased": {
    backgroundColor: "#81C784",
    color: "#000",
  },
  "& .MuiDataGrid-root": {
    overflowX: "auto",
  },
  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
    borderRight: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
    fontSize: "0.8rem",
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "nowrap",
  },
}));

// Helper to get the status field name (first column)
const getStatusField = (data) => {
  if (!data || data.length === 0) return "status";
  return Object.keys(data[0])[0]; // First column is the status field
};

// Helper to get week columns from data
const getWeekColumns = (data) => {
  if (!data || data.length === 0) return [];
  const statusField = getStatusField(data);
  return Object.keys(data[0]).filter((col) => col !== statusField);
};

// Helper to collect all unique status values from ALL datasets
const collectAllStatusValues = (...datasets) => {
  const statusSet = new Set();
  datasets.forEach((data) => {
    if (!data || data.length === 0) return;
    const statusField = getStatusField(data);
    data.forEach((row) => {
      if (row[statusField] && row[statusField] !== "Total") {
        statusSet.add(row[statusField]);
      }
    });
  });
  return Array.from(statusSet).sort();
};

// Helper to normalize data - ensure all status rows exist with consistent columns
const normalizeDataWithAllStatus = (data, allStatusValues, weekColumns, statusField) => {
  if (!data || data.length === 0) {
    // Return empty structure with all status rows as 0
    const normalizedData = allStatusValues.map((status) => {
      const row = { [statusField]: status };
      weekColumns.forEach((col) => (row[col] = 0));
      return row;
    });
    const totalRow = { [statusField]: "Total" };
    weekColumns.forEach((col) => (totalRow[col] = 0));
    normalizedData.push(totalRow);
    return normalizedData;
  }

  // Create map from existing data
  const dataMap = {};
  data.forEach((row) => {
    const sf = getStatusField(data);
    if (row[sf] && row[sf] !== "Total") {
      dataMap[row[sf]] = row;
    }
  });

  // Build normalized data with all status values
  const normalizedData = allStatusValues.map((status) => {
    const existingRow = dataMap[status];
    const row = { [statusField]: status };
    weekColumns.forEach((col) => {
      row[col] = existingRow ? (Number(existingRow[col]) || 0) : 0;
    });
    return row;
  });

  // Add total row
  const totalRow = { [statusField]: "Total" };
  weekColumns.forEach((col) => {
    totalRow[col] = normalizedData.reduce((sum, row) => sum + (Number(row[col]) || 0), 0);
  });
  normalizedData.push(totalRow);

  return normalizedData;
};

function PDPVCDPNew() {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  // Default `From Date` to 7 days before today
  const [selectedFromDate, setSelectedFromDate] = useState(dayjs().subtract(7, "day"));
  // EIPO category passed straight through to the API: ALL | EI | PO | Consulting
  const [selectedOption, setSelectedOption] = useState("ALL");
  const [chartView, setChartView] = useState(false);
  const [offOn, setSelectedOffshoreOnsite] = useState("All");
  const [loadingRegions, setRegionsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("All");

  // Compact table component used in PDP/VCDP page
  const PDPVCDPTable = ({ reportData, tableName }) => {
    const tableRef = useRef(null);

    const { rows, columns } = useMemo(() => {
      if (!reportData || reportData.length === 0) {
        return { rows: [], columns: [] };
      }

      const allKeys = Object.keys(reportData[0]);
      const statusField = allKeys[0];
      const weekColumns = allKeys.slice(1);

      const isZeroRow = (row) => {
        if (row[statusField] === "Total") return false;
        return weekColumns.every((col) => Number(row[col]) === 0);
      };

      const cols = allKeys.map((key, index) => {
        const isCurrentColumn = key.toLowerCase() === "current" || index === allKeys.length - 1;
        const previousColIndex = allKeys.findIndex((k) => k.toLowerCase() === "previous");

        return {
          field: key,
          headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
          minWidth: index === 0 ? 180 : 55,
          flex: index === 0 ? 0 : 1,
          align: index === 0 ? "left" : "center",
          headerAlign: index === 0 ? "left" : "center",
          cellClassName: (params) => {
            const isLastRow = params.row[statusField] === "Total";
            const isFirstColumn = index === 0;
            const isLastColumn = index === allKeys.length - 1;
            const numericValue = Number(params.value);
            const isNegative = !isLastRow && isLastColumn && !isNaN(numericValue) && numericValue < 0;
            const rowIsZero = isZeroRow(params.row);

            let comparisonClass = "";
            if (isCurrentColumn && !isLastRow && !rowIsZero && previousColIndex >= 0) {
              const prevColKey = allKeys[previousColIndex];
              const prevValue = Number(params.row[prevColKey]) || 0;
              const currValue = Number(params.value) || 0;
              if (currValue > prevValue) {
                comparisonClass = "value-increased";
              } else if (currValue < prevValue) {
                comparisonClass = "value-decreased";
              }
            }

            if (isLastRow) return "last-row" + (isFirstColumn ? " first-column" : "");
            if (rowIsZero && isFirstColumn) return "zero-row first-column";
            if (rowIsZero) return "zero-row";

            return `${isFirstColumn ? "first-column" : ""} ${isLastColumn ? "last-column" : ""} ${isNegative ? "negative-value" : ""} ${comparisonClass}`.trim();
          },
        };
      });

      const rws = reportData.map((row, index) => ({ ...row, id: index + 1 }));
      return { rows: rws, columns: cols };
    }, [reportData]);

    const tableHeight = 30 + rows.length * 30;

    return (
      <Box sx={{ position: "relative", p: 0.25 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: primaryColor,
            mb: 0.25,
            textAlign: "center",
            fontSize: "0.85rem",
          }}
        >
          {tableName}
        </Typography>
        <Box ref={tableRef} sx={{ height: tableHeight, width: "100%" }}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            rowHeight={40}
            columnHeaderHeight={42}
            hideFooter
            disableSelectionOnClick
            density="compact"
            sx={{
              fontSize: "0.75rem",
              "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.75rem !important" },
              "& .MuiDataGrid-cell": { fontSize: "0.75rem", padding: "0 4px" },
              "& .MuiDataGrid-filler": { display: "none" },
              "& .MuiDataGrid-virtualScroller": { overflow: "hidden !important" },
            }}
          />
        </Box>
      </Box>
    );
  };

  // Raw API data - both sources come pre-filtered by the selected EIPO category
  const [aPlusPDP, setAplusPDP] = useState([]);
  const [pStarPDP, setPStarPDP] = useState([]);
  const [aPlusVCDP, setAplusVCDP] = useState([]);
  const [pStarVCDP, setPStarVCDP] = useState([]);

  const fetchData = async (date) => {
    try {
      setLoading(true);
      const extDate = date.format("YYYY-MM-DD");
      const fromDateParam = selectedFromDate ? selectedFromDate.format("YYYY-MM-DD") : extDate;
      const baseURL = process.env.REACT_APP_API_BASE_URL;

      // PDP and CDeploy each use a single consolidated POST endpoint - the selected EIPO
      // category (ALL/EI/PO/Consulting) is applied by the DB function and both revenue
      // grade buckets (A+ and P*) come back in one response.
      const payload = {
        offOn,
        extDate,
        fromDate: fromDateParam,
        eipo: selectedOption,
        region: selectedRegion,
      };

      const [pdpRes, vcdpRes] = await Promise.all([
        axios.post(`${baseURL}/pdp/getPDPData`, payload),
        axios.post(`${baseURL}/vcdp/getVCDPData`, payload),
      ]);

      setAplusPDP(pdpRes.data?.aPlus || []);
      setPStarPDP(pdpRes.data?.pStar || []);
      setAplusVCDP(vcdpRes.data?.aPlus || []);
      setPStarVCDP(vcdpRes.data?.pStar || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };


  const fetchRegions = async (date) => {
    try {
      setRegionsLoading(true);
      const extDate = date.format("YYYY-MM-DD");
      const fromDateParam = selectedFromDate ? selectedFromDate.format("YYYY-MM-DD") : extDate;
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.get(`${baseURL}/api/pool/getPdpVcdpRegions`);
      setRegions(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setRegionsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      fetchData(selectedDate);
    }
  }, [selectedDate, selectedFromDate, offOn, selectedRegion, selectedOption]);

  useEffect(() => {
    fetchRegions(selectedDate);
  }, []);

  const handleChartViewToggle = (e) => {
    setChartView(e.target.checked);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleFromDateChange = (newDate) => {
    setSelectedFromDate(newDate);
  };

  const handleOffshoreOnsiteOptionChange = (event) => {
    setSelectedOffshoreOnsite(event.target.value);
  };

  const handleRegionsOptionChange = (event) => {
    setSelectedRegion(event.target.value);
  };

  // Compute display data based on selected option - normalize all tables with consistent status rows
  const displayData = useMemo(() => {
    // Collect all unique status values from ALL datasets
    const allStatusValues = collectAllStatusValues(
      aPlusPDP, pStarPDP, aPlusVCDP, pStarVCDP
    );

    // Get week columns from first available dataset
    const sampleData = [aPlusPDP, pStarPDP, aPlusVCDP, pStarVCDP]
      .find(d => d && d.length > 0);
    const weekColumns = sampleData ? getWeekColumns(sampleData) : [];
    const statusField = sampleData ? getStatusField(sampleData) : "status";

    // "ALL" needs no prefix, every other category labels its tables
    const prefix = selectedOption === "ALL" ? "" : `${selectedOption} `;

    // Both sources are already filtered server side by the selected EIPO category
    return {
      tables: [
        { name: `${prefix}A+ PDP`, data: normalizeDataWithAllStatus(aPlusPDP, allStatusValues, weekColumns, statusField) },
        { name: `${prefix}P* PDP`, data: normalizeDataWithAllStatus(pStarPDP, allStatusValues, weekColumns, statusField) },
        { name: `${prefix}A+ CDeploy`, data: normalizeDataWithAllStatus(aPlusVCDP, allStatusValues, weekColumns, statusField) },
        { name: `${prefix}P* CDeploy`, data: normalizeDataWithAllStatus(pStarVCDP, allStatusValues, weekColumns, statusField) },
      ],
    };
  }, [
    selectedOption,
    aPlusPDP,
    pStarPDP,
    aPlusVCDP,
    pStarVCDP,
  ]);

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Grid container alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Grid item size={6} md={6}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: primaryColor,
              textAlign: "left",
            }}
          >
            Periodic Report : PDP & VCDP
          </Typography>
        </Grid>
        <Grid item size={6} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={<Switch checked={chartView} onChange={handleChartViewToggle} color="primary" />}
            label="Chart View"
            sx={{ ml: 1 }}
          />
        </Grid>
        <Grid item size={12} md={12} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Paper sx={{ p: 1, borderRadius: 1, overflowX: 'auto', display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap', minWidth: 0 }}>
            <TextField
              label="From Date1"
              type="date"
              size="small"
              value={selectedFromDate.format("YYYY-MM-DD")}
              onChange={(e) => handleFromDateChange(dayjs(e.target.value))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label="To Date"
              type="date"
              size="small"
              value={selectedDate.format("YYYY-MM-DD")}
              onChange={(e) => handleDateChange(dayjs(e.target.value))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="source-label">Source</InputLabel>
              <Select
                labelId="source-label"
                id="source-select"
                value={selectedOption}
                label="Source"
                onChange={handleOptionChange}
                size="small"
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="EI">EI</MenuItem>
                <MenuItem value="PO">PO</MenuItem>
                <MenuItem value="Consulting">Consulting</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="offshore-onsite-label">Offshore/Onsite</InputLabel>
              <Select
                labelId="offshore-onsite-label"
                id="offshore-onsite-select"
                value={offOn}
                label="Offshore/Onsite"
                onChange={handleOffshoreOnsiteOptionChange}
                size="small"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Offshore">Offshore</MenuItem>
                <MenuItem value="Onsite">Onsite</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="regions-label">Regions</InputLabel>
              <Select
                labelId="regions-label"
                id="regions-select"
                value={selectedRegion}
                label="Regions"
                onChange={handleRegionsOptionChange}
                size="small"
              >
                <MenuItem value="All">All</MenuItem>
                {regions.map((option) => (
                  <MenuItem key={option.region} value={option.region}>
                    {option.region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </Paper>
        </Grid>
      </Grid>

      {/* Tables Display - Compact 2x2 Grid or Charts when chartView=true */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={30} />
        </Box>
      ) : chartView ? (
        <Grid container columnSpacing={2} rowSpacing={0.5}>
          {displayData.tables.map((t, idx) => {
            const tbl = t.data || [];
            const allKeys = tbl && tbl[0] ? Object.keys(tbl[0]) : [];
            const statusField = allKeys[0] || "status";
            const prevKey = allKeys.find(k => k.toLowerCase() === "previous") || allKeys[allKeys.length - 2];
            const currKey = allKeys[allKeys.length - 1];
            const chartData = (tbl || []).filter(r => r[statusField] !== "Total").map(r => ({
              status: r[statusField],
              previous: Number(r[prevKey]) || 0,
              current: Number(r[currKey]) || 0,
            }));

            return (
              <Grid item xs={6} key={idx}>
                <Paper sx={{ p: 0.5, border: `1px solid ${darkGrey}`, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: primaryColor, mb: 0.5 }}>{t.name}</Typography>
                  <Box sx={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                        <XAxis dataKey="status" interval={0} angle={-30} textAnchor="end" height={60} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="previous" fill="#1976d2" />
                        <Bar dataKey="current" fill="#2e7d32" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Grid container columnSpacing={2} rowSpacing={0.5}>
          {/* First Row - PDP Tables */}
          <Grid item xs={6}>
            <Paper sx={{ p: 0.5, border: `1px solid ${darkGrey}`, borderRadius: 1 }}>
              <PDPVCDPTable
                reportData={displayData.tables[0].data}
                tableName={displayData.tables[0].name}
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 0.5, border: `1px solid ${darkGrey}`, borderRadius: 1 }}>
              <PDPVCDPTable
                reportData={displayData.tables[1].data}
                tableName={displayData.tables[1].name}
              />
            </Paper>
          </Grid>

          {/* Second Row - VCDP/CDeploy Tables */}
          <Grid item xs={6}>
            <Paper sx={{ p: 0.5, border: `1px solid ${darkGrey}`, borderRadius: 1 }}>
              <PDPVCDPTable
                reportData={displayData.tables[2].data}
                tableName={displayData.tables[2].name}
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 0.5 }}>
              <PDPVCDPTable
                reportData={displayData.tables[3].data}
                tableName={displayData.tables[3].name}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default PDPVCDPNew;
